import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import apiClient from '@/utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Users, RefreshCw } from 'lucide-react';

// Consistent brand colors from logo
const COLORS = {
  primary: '#192650',
  accent: '#0DA891',
  background: '#F8FAFC',
  border: '#E2E8F0'
};

const UserManagement = () => {
  const { user } = useAuth();
  
  // Main tab state - Users List, Teams
  const [mainTab, setMainTab] = useState('users');
  
  // ===== USER LIST STATE =====
  const [users, setUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [userSubTab, setUserSubTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [userToDelete, setUserToDelete] = useState(null);
  const [transferToCounselor, setTransferToCounselor] = useState('');
  const [permanentDelete, setPermanentDelete] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteStats, setDeleteStats] = useState(null);
  const [loadingDeleteStats, setLoadingDeleteStats] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'counselor',
    partner_office_id: null
  });

  // ===== TEAMS STATE =====
  const [teams, setTeams] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [availableCounselors, setAvailableCounselors] = useState([]);
  const [assignedCounselors, setAssignedCounselors] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [showCreateTeamDialog, setShowCreateTeamDialog] = useState(false);
  const [showEditTeamDialog, setShowEditTeamDialog] = useState(false);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  
  const [teamForm, setTeamForm] = useState({
    name: '',
    leader_id: '',
    color: '#3b82f6',
    description: ''
  });
  
  const [memberForm, setMemberForm] = useState({
    user_id: '',
    reason: ''
  });
  
  // Redistribute leads state
  const [showRedistributeDialog, setShowRedistributeDialog] = useState(false);
  const [redistributeUser, setRedistributeUser] = useState(null);
  const [redistributing, setRedistributing] = useState(false);

  // Color options for teams
  const colorOptions = [
    { value: '#3b82f6', label: 'Blue', color: 'bg-blue-500' },
    { value: '#22c55e', label: 'Green', color: 'bg-green-500' },
    { value: '#f59e0b', label: 'Orange', color: 'bg-orange-500' },
    { value: '#ef4444', label: 'Red', color: 'bg-red-500' },
    { value: '#8b5cf6', label: 'Purple', color: 'bg-purple-500' },
    { value: '#ec4899', label: 'Pink', color: 'bg-pink-500' },
  ];

  // ===== FETCH FUNCTIONS =====
  useEffect(() => {
    fetchUsers();
    if (user?.role === 'admin') {
      fetchInactiveUsers();
    }
    fetchTeams();
    fetchCounselors();
  }, []);

  const fetchUsers = async () => {
    try {
      // Include inactive users so admins can reactivate or permanently delete them
      // from the same list (UserCard already shows Active/Inactive badge + toggle action)
      const response = await apiClient.get('/users?include_inactive=true');
      // Sort: active users first, then inactive (alphabetical within each group)
      const sorted = [...response.data].sort((a, b) => {
        if (a.is_active === b.is_active) {
          return (a.full_name || '').localeCompare(b.full_name || '');
        }
        return a.is_active === false ? 1 : -1;
      });
      setUsers(sorted);
    } catch (error) {
      toast.error('Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const fetchInactiveUsers = async () => {
    try {
      const response = await apiClient.get('/users?include_inactive=true');
      const inactive = response.data.filter(u => u.is_active === false);
      setInactiveUsers(inactive);
    } catch (error) {
      console.error('Error fetching inactive users:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await apiClient.get('/teams');
      setTeams(response.data.teams || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setTeamsLoading(false);
    }
  };

  const fetchCounselors = async () => {
    try {
      const availableResponse = await apiClient.get('/teams/available-counselors');
      setAvailableCounselors(availableResponse.data.available || []);
      setAssignedCounselors(availableResponse.data.assigned || []);
      
      const response = await apiClient.get('/users');
      const counselorList = response.data.filter(u =>
        ((u.role === 'counselor' || u.role === 'team_leader')
         || (u.role === 'operating_head' && u.also_counselor)) && u.is_active
      );
      setCounselors(counselorList);
    } catch (error) {
      console.error('Failed to fetch counselors:', error);
    }
  };

  const fetchTeamDetails = async (teamId) => {
    try {
      const response = await apiClient.get(`/teams/${teamId}`);
      setSelectedTeam(response.data);
      setTeamMembers(response.data.members || []);
    } catch (error) {
      toast.error('Failed to fetch team details');
    }
  };

  // ===== USER HANDLERS =====
  const handleAddUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await apiClient.post('/auth/register', newUser);
      setUsers([response.data.user, ...users]);
      setShowAddDialog(false);
      setNewUser({ email: '', password: '', full_name: '', role: 'counselor', partner_office_id: null });
      toast.success('User added successfully!');
      fetchCounselors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = (userItem) => {
    setEditingUser({
      id: userItem.id,
      full_name: userItem.full_name,
      email: userItem.email,
      role: userItem.role,
      phone: userItem.phone || '',
      also_counselor: !!userItem.also_counselor,
      include_in_auto_assign: !!userItem.include_in_auto_assign,
    });
    setShowEditDialog(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const payload = {
        full_name: editingUser.full_name,
        email: editingUser.email,
        role: editingUser.role,
        phone: editingUser.phone,
      };
      // Include dual-role fields in the single PUT so backend updates everything atomically.
      if (editingUser.role === 'operating_head') {
        payload.also_counselor = !!editingUser.also_counselor;
        payload.include_in_auto_assign = !!(editingUser.also_counselor && editingUser.include_in_auto_assign);
      } else {
        payload.also_counselor = false;
        payload.include_in_auto_assign = false;
      }
      const response = await apiClient.put(`/users/${editingUser.id}`, payload);
      setUsers(users.map(u => u.id === editingUser.id ? response.data : u));
      setShowEditDialog(false);
      setEditingUser(null);
      toast.success('User updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleUserStatus = async (userItem) => {
    try {
      const newStatus = !userItem.is_active;
      await apiClient.put(`/users/${userItem.id}/status`, { is_active: newStatus });
      // Re-fetch so the active/inactive partitioning + sort order stays consistent
      await fetchUsers();
      toast.success(`User ${newStatus ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userToDeleteParam) => {
    if (userToDeleteParam.id === user.id) {
      toast.error('Cannot delete your own account');
      return;
    }
    setUserToDelete(userToDeleteParam);
    setTransferToCounselor('');
    setPermanentDelete(true);
    setDeleteStats(null);
    setShowDeleteDialog(true);
    // Fetch lead stats so admin sees what gets reassigned vs retained
    setLoadingDeleteStats(true);
    try {
      const res = await apiClient.get(`/users/${userToDeleteParam.id}/lead-stats`);
      setDeleteStats(res.data);
    } catch (err) {
      // Non-fatal: dialog still opens, just without stats
      setDeleteStats({ active_leads: 0, bad_leads: 0, converted_admissions: 0, total_to_transfer: 0 });
    } finally {
      setLoadingDeleteStats(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    // For counselors, require explicit transfer choice (specific OR split-equally)
    if (userToDelete.role === 'counselor') {
      const otherActiveCounselors = users.filter(u => u.role === 'counselor' && u.id !== userToDelete.id && u.is_active);
      const totalToTransfer = Number(deleteStats?.total_to_transfer || 0);
      if (otherActiveCounselors.length > 0 && totalToTransfer > 0 && !transferToCounselor) {
        toast.error("Pick a counselor or 'Split equally among active counsellors' to transfer the leads.");
        return;
      }
      // Toast preview before destructive action
      if (totalToTransfer > 0 && transferToCounselor) {
        const isSplit = transferToCounselor === '__split_equally__';
        const targetName = isSplit
          ? `${otherActiveCounselors.length} active counsellor${otherActiveCounselors.length === 1 ? '' : 's'}`
          : (otherActiveCounselors.find(c => c.id === transferToCounselor)?.full_name || 'selected counselor');
        const perPersonHint = isSplit && otherActiveCounselors.length > 0
          ? ` (~${Math.ceil(totalToTransfer / otherActiveCounselors.length)} per counsellor)`
          : '';
        const convertedNote = Number(deleteStats?.converted_admissions || 0) > 0
          ? ` ${deleteStats.converted_admissions} converted admission${deleteStats.converted_admissions === 1 ? '' : 's'} will retain '${userToDelete.full_name}' as the original counsellor.`
          : '';
        toast.info(
          `Reassigning ${totalToTransfer} lead${totalToTransfer === 1 ? '' : 's'} (${deleteStats?.active_leads || 0} active + ${deleteStats?.bad_leads || 0} bad) to ${targetName}${perPersonHint}.${convertedNote}`,
          { duration: 4500 }
        );
      }
    }
    try {
      setIsSubmitting(true);
      const deleteParams = new URLSearchParams();
      deleteParams.append('permanent', permanentDelete);
      if (transferToCounselor) {
        deleteParams.append('transfer_to', transferToCounselor);
      }
      await apiClient.delete(`/users/${userToDelete.id}?${deleteParams.toString()}`);
      if (permanentDelete) {
        // Hard delete: remove from list entirely
        setUsers(users.filter(u => u.id !== userToDelete.id));
        setInactiveUsers(inactiveUsers.filter(u => u.id !== userToDelete.id));
      } else {
        // Soft delete: re-fetch so the user appears as Inactive in the list
        await fetchUsers();
        if (user?.role === 'admin') {
          fetchInactiveUsers();
        }
      }
      toast.success(`User ${permanentDelete ? 'permanently deleted' : 'deactivated'} successfully!`);
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchCounselors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReactivateUser = async (userItem) => {
    try {
      setIsSubmitting(true);
      await apiClient.put(`/users/${userItem.id}/reactivate`);
      // Remove from inactive list and add to active list
      setInactiveUsers(inactiveUsers.filter(u => u.id !== userItem.id));
      // Refresh users list to get the reactivated user
      await fetchUsers();
      toast.success(`User ${userItem.full_name} reactivated successfully!`);
      fetchCounselors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate user');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ===== PASSWORD RESET HANDLER =====
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await apiClient.put(`/users/${userToResetPassword.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(`Password reset successfully for ${userToResetPassword.full_name}`);
      setShowResetPasswordDialog(false);
      setUserToResetPassword(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openResetPasswordDialog = (userItem) => {
    setUserToResetPassword(userItem);
    setNewPassword('');
    setShowResetPasswordDialog(true);
  };

  // ===== REDISTRIBUTE LEADS HANDLER =====
  const handleRedistributeLeads = async () => {
    if (!redistributeUser) return;
    
    setRedistributing(true);
    try {
      const response = await apiClient.post(`/admin/redistribute-leads?source_counselor_id=${redistributeUser.id}`);
      toast.success(response.data.message);
      setShowRedistributeDialog(false);
      setRedistributeUser(null);
      fetchUsers(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redistribute leads');
    } finally {
      setRedistributing(false);
    }
  };

  const openRedistributeDialog = (userItem) => {
    setRedistributeUser(userItem);
    setShowRedistributeDialog(true);
  };


  // ===== TEAM HANDLERS =====
  const handleCreateTeam = async () => {
    try {
      await apiClient.post('/teams', teamForm);
      toast.success('Team created successfully!');
      setShowCreateTeamDialog(false);
      setTeamForm({ name: '', leader_id: '', color: '#3b82f6', description: '' });
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleUpdateTeam = async () => {
    try {
      await apiClient.put(`/teams/${selectedTeam.id}`, teamForm);
      toast.success('Team updated successfully!');
      setShowEditTeamDialog(false);
      fetchTeams();
      if (selectedTeam) fetchTeamDetails(selectedTeam.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team');
    }
  };

  const handleAddMember = async () => {
    try {
      await apiClient.post(`/teams/${selectedTeam.id}/members`, memberForm);
      toast.success('Member added to team!');
      setMemberForm({ user_id: '', reason: '' });
      fetchTeams();
      fetchTeamDetails(selectedTeam.id);
      fetchCounselors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    const reason = prompt(`Why are you removing ${userName} from the team?`);
    if (!reason) return;
    try {
      await apiClient.delete(`/teams/${selectedTeam.id}/members/${userId}`, { params: { reason } });
      toast.success('Member removed from team');
      fetchTeams();
      fetchTeamDetails(selectedTeam.id);
      fetchCounselors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleDeleteTeam = async (team) => {
    if (!window.confirm(`Delete "${team.name}"? This will remove all members and unassign leads.`)) return;
    try {
      await apiClient.delete(`/teams/${team.id}`);
      toast.success('Team deleted successfully');
      fetchTeams();
      fetchCounselors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  const handleToggleTeamStatus = async (team) => {
    const newStatus = team.status === 'active' ? 'inactive' : 'active';
    try {
      await apiClient.put(`/teams/${team.id}`, { status: newStatus });
      toast.success(`Team ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team status');
    }
  };

  const openEditTeamDialog = (team) => {
    setSelectedTeam(team);
    setTeamForm({
      name: team.name,
      leader_id: team.leader_id || '',
      color: team.color,
      description: team.description || ''
    });
    setShowEditTeamDialog(true);
  };

  const openMembersDialog = (team) => {
    setSelectedTeam(team);
    fetchTeamDetails(team.id);
    setShowMembersDialog(true);
  };

  // ===== HELPERS =====
  const getRoleDisplayName = (role) => {
    const roleMap = { 'admin': 'Administrator', 'accountant': 'Accountant', 'operating_head': 'Operating Head', 'counselor': 'Counselor', 'documentation_team': 'Documentation Team', 'telecaller': 'Telecaller', 'team_leader': 'Team Leader' };
    return roleMap[role] || role;
  };

  const getRoleColor = (role) => {
    const colorMap = { 'admin': 'bg-red-100 text-red-800', 'operating_head': 'bg-purple-100 text-purple-800', 'accountant': 'bg-green-100 text-green-800', 'counselor': 'bg-blue-100 text-blue-800', 'documentation_team': 'bg-amber-100 text-amber-800', 'telecaller': 'bg-cyan-100 text-cyan-800', 'team_leader': 'bg-indigo-100 text-indigo-800' };
    return colorMap[role] || 'bg-gray-100 text-gray-800';
  };

  // ===== ACCESS CHECK =====
  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-lock text-red-600 text-2xl"></i>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-500">You don't have permission to manage users.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-3 sm:p-0 min-h-screen" style={{ backgroundColor: COLORS.background }}>
      {/* Header - Consistent with other modules */}
      <div className="sticky top-0 z-10 rounded-xl p-3 sm:p-4" style={{ backgroundColor: COLORS.primary }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.accent }}>
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-semibold text-white">User Management</h1>
              <p className="text-[10px] sm:text-xs text-white/70 hidden sm:block">Manage users, roles, and teams</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Tabs: Users List | Teams with Action Buttons */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <div className="flex items-center justify-between gap-2 mb-3">
          <TabsList className="grid grid-cols-2 h-9 w-auto" style={{ borderColor: COLORS.border }}>
            <TabsTrigger value="users" data-testid="users-tab" className="text-xs px-3">
              <i className="fas fa-users mr-1"></i> Users
            </TabsTrigger>
            <TabsTrigger value="teams" data-testid="teams-tab" className="text-xs px-3">
              <i className="fas fa-users-cog mr-1"></i> Teams
            </TabsTrigger>
          </TabsList>
          
          {/* Action buttons - change based on active tab */}
          <div className="flex gap-1">
            <Button 
              variant="outline" 
              onClick={mainTab === 'users' ? fetchUsers : fetchTeams} 
              disabled={mainTab === 'users' ? loading : teamsLoading} 
              size="sm" 
              className="h-8 w-8 p-0"
              style={{ borderColor: COLORS.border }}
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            {mainTab === 'users' ? (
              <Button 
                className="h-8 text-xs px-3" 
                style={{ backgroundColor: COLORS.accent }}
                onClick={() => setShowAddDialog(true)} 
                data-testid="add-user-button"
              >
                <i className="fas fa-plus mr-1"></i> Add
              </Button>
            ) : (
              <Button 
                className="h-8 text-xs px-3" 
                style={{ backgroundColor: COLORS.primary }}
                onClick={() => setShowCreateTeamDialog(true)} 
                data-testid="create-team-button"
              >
                <i className="fas fa-plus mr-1"></i> Create
              </Button>
            )}
          </div>
        </div>

        {/* ===== USERS LIST TAB ===== */}
        <TabsContent value="users" className="mt-0">

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {users.map((userItem) => (
              <UserCard 
                key={userItem.id} 
                userItem={userItem} 
                currentUser={user}
                onEdit={handleEditUser}
                onToggleStatus={handleToggleUserStatus}
                onDelete={handleDeleteUser}
                onResetPassword={openResetPasswordDialog}
                onRedistribute={openRedistributeDialog}
                getRoleColor={getRoleColor}
                getRoleDisplayName={getRoleDisplayName}
              />
            ))}
          </div>
          {users.length === 0 && <EmptyUsersState onAdd={() => setShowAddDialog(true)} />}
        </TabsContent>

        {/* ===== TEAMS TAB ===== */}
        <TabsContent value="teams" className="mt-0">
          {/* Teams Grid */}
          {teamsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading teams...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
              {teams.map((team) => {
                const isInactive = team.status === 'inactive';
                const isEmpty = team.member_count === 0;
                let badgeText = 'active', badgeClass = 'bg-green-500';
                if (isInactive) { badgeText = 'inactive'; badgeClass = 'bg-red-500'; }
                else if (isEmpty) { badgeText = 'empty'; badgeClass = 'bg-gray-400'; }

                return (
                  <Card key={team.id} className={`hover:shadow-lg transition-shadow ${isInactive ? 'opacity-60' : ''}`} data-testid={`team-card-${team.id}`}>
                    <CardContent className="p-3 sm:p-4">
                      {/* Team Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: team.color }}></div>
                          <h3 className="font-semibold text-sm truncate">{team.name}</h3>
                        </div>
                        <Badge className={`${badgeClass} text-[10px] px-1.5 py-0.5`}>{badgeText}</Badge>
                      </div>
                      <p className="text-[10px] text-gray-500 mb-2 line-clamp-1">{team.description || 'No description'}</p>
                      
                      {/* Stats Row */}
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div className="text-center p-1.5 bg-blue-50 rounded">
                          <div className="text-base font-bold text-blue-600">{team.member_count}</div>
                          <div className="text-[9px] text-gray-500">Members</div>
                        </div>
                        <div className="text-center p-1.5 bg-green-50 rounded">
                          <div className="text-xs font-semibold text-green-600 truncate">{team.leader_name || 'No Leader'}</div>
                          <div className="text-[9px] text-gray-500">Team Leader</div>
                        </div>
                      </div>
                      
                      {/* Action Buttons - Compact Row */}
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="flex-1 h-7 text-[10px] px-1" onClick={() => openEditTeamDialog(team)}>
                          <i className="fas fa-edit mr-0.5"></i> Edit
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1 h-7 text-[10px] px-1" onClick={() => openMembersDialog(team)}>
                          <i className="fas fa-users mr-0.5"></i> Members
                        </Button>
                        <Button size="sm" variant="outline" className={`h-7 w-7 p-0 ${team.status === 'active' ? 'text-orange-600' : 'text-green-600'}`} onClick={() => handleToggleTeamStatus(team)}>
                          <i className={`fas ${team.status === 'active' ? 'fa-pause' : 'fa-play'} text-[10px]`}></i>
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDeleteTeam(team)}>
                          <i className="fas fa-trash text-[10px]"></i>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          {!teamsLoading && teams.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-users-cog text-gray-400 text-2xl"></i>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No teams yet</h3>
                <p className="text-gray-500 mb-4">Create your first team to organize counselors</p>
                <Button onClick={() => setShowCreateTeamDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                  <i className="fas fa-plus mr-2"></i> Create Team
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ===== DIALOGS ===== */}
      
      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new user account.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input value={newUser.full_name} onChange={(e) => setNewUser({...newUser, full_name: e.target.value})} required data-testid="add-user-name-input" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required data-testid="add-user-email-input" />
            </div>
            <div>
              <Label>Password *</Label>
              <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required minLength={6} data-testid="add-user-password-input" />
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                <SelectTrigger data-testid="add-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="operating_head">Operating Head</SelectItem>
                  <SelectItem value="accountant">Accountant</SelectItem>
                  <SelectItem value="counselor">Counselor</SelectItem>
                  <SelectItem value="documentation_team">Documentation Team</SelectItem>
                  <SelectItem value="telecaller">Telecaller</SelectItem>
                  <SelectItem value="team_leader">Team Leader</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Adding...' : 'Add User'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input value={editingUser?.full_name || ''} onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})} required />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={editingUser?.email || ''} onChange={(e) => setEditingUser({...editingUser, email: e.target.value})} required />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={editingUser?.phone || ''} onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})} placeholder="Optional" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={editingUser?.role || ''} onValueChange={(value) => setEditingUser({...editingUser, role: value})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="counselor">Counselor</SelectItem>
                  <SelectItem value="accountant">Accountant</SelectItem>
                  <SelectItem value="operating_head">Operating Head</SelectItem>
                  <SelectItem value="documentation_team">Documentation Team</SelectItem>
                  <SelectItem value="telecaller">Telecaller</SelectItem>
                  <SelectItem value="team_leader">Team Leader</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editingUser?.role === 'operating_head' && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2"
                   data-testid="dual-role-section">
                <div className="text-xs font-semibold text-indigo-900 uppercase tracking-wide">
                  <i className="fas fa-exchange-alt mr-1.5"></i> Dual Role
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editingUser?.also_counselor}
                    onChange={(e) => setEditingUser({
                      ...editingUser,
                      also_counselor: e.target.checked,
                      include_in_auto_assign: e.target.checked ? editingUser?.include_in_auto_assign : false,
                    })}
                    className="mt-0.5 w-4 h-4"
                    data-testid="also-counselor-checkbox"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-gray-800">Also acts as a Counselor</span>
                    <div className="text-[11px] text-gray-500">User can switch between Operating Head and Counselor modes from the topbar avatar menu.</div>
                  </span>
                </label>
                {editingUser?.also_counselor && (
                  <label className="flex items-start gap-2 cursor-pointer pl-6">
                    <input
                      type="checkbox"
                      checked={!!editingUser?.include_in_auto_assign}
                      onChange={(e) => setEditingUser({...editingUser, include_in_auto_assign: e.target.checked})}
                      className="mt-0.5 w-4 h-4"
                      data-testid="include-in-auto-assign-checkbox"
                    />
                    <span className="text-sm">
                      <span className="font-medium text-gray-800">Include in auto-assignment when in Counselor mode</span>
                      <div className="text-[11px] text-gray-500">When ON, this user receives new leads via round-robin / Power Hours only while their active role is Counselor.</div>
                    </span>
                  </label>
                )}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Updating...' : 'Update User'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="text-red-600"><i className="fas fa-exclamation-triangle mr-2"></i>Delete User</DialogTitle>
            <DialogDescription>Deleting <strong>{userToDelete?.full_name}</strong></DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <input type="checkbox" id="permanent-delete" checked={permanentDelete} onChange={(e) => setPermanentDelete(e.target.checked)} className="w-4 h-4" />
                <Label htmlFor="permanent-delete" className="font-semibold cursor-pointer">Permanent Delete (Recommended)</Label>
              </div>
              <p className="text-sm text-blue-800">{permanentDelete ? 'User will be completely removed. Email can be reused.' : 'User will be marked inactive. Email cannot be reused.'}</p>
            </div>

            {/* Lead-stats breakdown — what gets reassigned vs retained */}
            {userToDelete?.role === 'counselor' && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="delete-user-lead-stats">
                <div className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  <i className="fas fa-chart-bar mr-1.5 text-slate-500"></i>
                  Lead breakdown for {userToDelete?.full_name}
                </div>
                {loadingDeleteStats ? (
                  <div className="text-xs text-gray-500 py-2"><i className="fas fa-spinner fa-spin mr-2"></i>Loading…</div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded bg-white border border-emerald-200 p-2 text-center" data-testid="stat-active-leads">
                      <div className="text-[10px] text-gray-500 uppercase">Active leads</div>
                      <div className="text-lg font-bold text-emerald-700">{deleteStats?.active_leads ?? 0}</div>
                      <div className="text-[10px] text-gray-500">will reassign</div>
                    </div>
                    <div className="rounded bg-white border border-rose-200 p-2 text-center" data-testid="stat-bad-leads">
                      <div className="text-[10px] text-gray-500 uppercase">Bad leads</div>
                      <div className="text-lg font-bold text-rose-700">{deleteStats?.bad_leads ?? 0}</div>
                      <div className="text-[10px] text-gray-500">will reassign</div>
                    </div>
                    <div className="rounded bg-white border border-amber-200 p-2 text-center" data-testid="stat-converted-admissions">
                      <div className="text-[10px] text-gray-500 uppercase">Admissions</div>
                      <div className="text-lg font-bold text-amber-700">{deleteStats?.converted_admissions ?? 0}</div>
                      <div className="text-[10px] text-gray-500">keep original name</div>
                    </div>
                  </div>
                )}
                {Number(deleteStats?.converted_admissions || 0) > 0 && (
                  <p className="text-[11px] text-amber-800 mt-2 flex items-start gap-1.5">
                    <i className="fas fa-shield-alt mt-0.5"></i>
                    <span>
                      Converted admissions stay attributed to <strong>{userToDelete?.full_name}</strong> for historical accuracy — they will <strong>not</strong> be reassigned.
                    </span>
                  </p>
                )}
              </div>
            )}

            {userToDelete?.role === 'counselor' && (() => {
              // Eligible to receive reassigned leads: any active counsellor + any active
              // dual-role Operating Head (also_counselor=true). Exclude the user being deleted.
              const available = users.filter(u =>
                u.id !== userToDelete?.id && u.is_active && (
                  u.role === 'counselor' ||
                  (u.role === 'operating_head' && u.also_counselor)
                )
              );
              const totalToTransfer = Number(deleteStats?.total_to_transfer || 0);
              if (available.length === 0) return null;
              if (totalToTransfer === 0) {
                return (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
                    <i className="fas fa-check-circle mr-1.5"></i>
                    No active or bad leads to transfer. Safe to delete directly.
                  </div>
                );
              }
              return (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <Label>
                    Transfer Leads To <span className="text-red-600">*</span>
                  </Label>
                  <p className="text-xs text-yellow-800 mt-1 mb-2">
                    <i className="fas fa-info-circle mr-1"></i>
                    {totalToTransfer} lead{totalToTransfer === 1 ? '' : 's'} will be reassigned. Pick a specific counsellor or split equally across all active counsellors.
                  </p>
                  <Select value={transferToCounselor} onValueChange={setTransferToCounselor}>
                    <SelectTrigger className="bg-white mt-2" data-testid="delete-user-transfer-select">
                      <SelectValue placeholder="Choose a counselor or split equally" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__split_equally__" data-testid="transfer-split-equally">
                        <span className="flex items-center gap-2">
                          <i className="fas fa-random text-emerald-600"></i>
                          <span>Split equally among active counsellors ({available.length})</span>
                        </span>
                      </SelectItem>
                      {available.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.full_name}
                          {c.role === 'operating_head' && (
                            <span className="ml-2 text-[10px] font-semibold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">OH</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={isSubmitting}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDeleteUser} disabled={isSubmitting}>
              {isSubmitting ? 'Deleting...' : permanentDelete ? 'Permanently Delete' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
            <DialogDescription>
              Set a new password for {userToResetPassword?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>User Email</Label>
              <Input value={userToResetPassword?.email || ''} disabled className="bg-gray-100" />
            </div>
            <div>
              <Label>New Password *</Label>
              <Input 
                type="password"
                value={newPassword} 
                onChange={(e) => setNewPassword(e.target.value)} 
                placeholder="Enter new password (min 4 characters)"
                data-testid="new-password-input"
              />
            </div>
            <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              Make sure to share the new password with the user securely.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordDialog(false)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={!newPassword || newPassword.length < 4 || isSubmitting} data-testid="reset-password-btn">
              {isSubmitting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redistribute Leads Dialog */}
      <Dialog open={showRedistributeDialog} onOpenChange={setShowRedistributeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-orange-600">
              <i className="fas fa-share-alt mr-2"></i>
              Redistribute Leads
            </DialogTitle>
            <DialogDescription>
              Equally distribute all active leads from {redistributeUser?.full_name} to all other active counselors.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-800">
                <i className="fas fa-info-circle mr-2"></i>
                This will redistribute all non-converted, non-bad leads from <strong>{redistributeUser?.full_name}</strong> equally among all active counselors.
              </p>
              <p className="text-xs text-orange-600 mt-2">
                Bad leads, converted leads, and future prospect leads will NOT be redistributed.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedistributeDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleRedistributeLeads} 
              disabled={redistributing}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {redistributing ? (
                <><i className="fas fa-spinner fa-spin mr-2"></i> Redistributing...</>
              ) : (
                <><i className="fas fa-share-alt mr-2"></i> Redistribute Leads</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Team Dialog */}
      <Dialog open={showCreateTeamDialog} onOpenChange={setShowCreateTeamDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Team</DialogTitle>
            <DialogDescription>Add a new team to organize counselors</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Team Name *</Label>
              <Input value={teamForm.name} onChange={(e) => setTeamForm({...teamForm, name: e.target.value})} placeholder="e.g., Sales Team" />
            </div>
            <div>
              <Label>Team Leader (Optional)</Label>
              <Select value={teamForm.leader_id || "none"} onValueChange={(v) => setTeamForm({...teamForm, leader_id: v === "none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Select team leader" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Leader</SelectItem>
                  {counselors.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Team Color</Label>
              <div className="flex gap-2 mt-2">
                {colorOptions.map(c => (
                  <button key={c.value} className={`w-8 h-8 rounded-full ${c.color} ${teamForm.color === c.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                    onClick={() => setTeamForm({...teamForm, color: c.value})} type="button" />
                ))}
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={teamForm.description} onChange={(e) => setTeamForm({...teamForm, description: e.target.value})} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTeamDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTeam} disabled={!teamForm.name}>Create Team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={showEditTeamDialog} onOpenChange={setShowEditTeamDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Team Name *</Label>
              <Input value={teamForm.name} onChange={(e) => setTeamForm({...teamForm, name: e.target.value})} />
            </div>
            <div>
              <Label>Team Leader</Label>
              <Select value={teamForm.leader_id || "none"} onValueChange={(v) => setTeamForm({...teamForm, leader_id: v === "none" ? "" : v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Leader</SelectItem>
                  {teamMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Team Color</Label>
              <div className="flex gap-2 mt-2">
                {colorOptions.map(c => (
                  <button key={c.value} className={`w-8 h-8 rounded-full ${c.color} ${teamForm.color === c.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                    onClick={() => setTeamForm({...teamForm, color: c.value})} type="button" />
                ))}
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={teamForm.description} onChange={(e) => setTeamForm({...teamForm, description: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTeamDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateTeam}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Team Members - {selectedTeam?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Current Members ({teamMembers.length})</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {teamMembers.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">{member.full_name}</div>
                      <div className="text-sm text-gray-500">{member.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {member.is_leader && <Badge className="bg-blue-500">Team Leader</Badge>}
                      {!member.is_leader && (
                        <Button size="sm" variant="destructive" onClick={() => handleRemoveMember(member.id, member.full_name)}>
                          <i className="fas fa-trash"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {teamMembers.length === 0 && <p className="text-gray-500 text-center py-4">No members yet</p>}
              </div>
            </div>
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Add New Member</h3>
              <div className="space-y-3">
                <Select value={memberForm.user_id} onValueChange={(v) => setMemberForm({...memberForm, user_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Choose a counselor" /></SelectTrigger>
                  <SelectContent>
                    {availableCounselors.length === 0 ? (
                      <div className="p-2 text-sm text-gray-500 text-center">No available counselors</div>
                    ) : (
                      availableCounselors.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)
                    )}
                  </SelectContent>
                </Select>
                {availableCounselors.length === 0 && assignedCounselors.length > 0 && (
                  <p className="text-xs text-amber-600"><i className="fas fa-info-circle mr-1"></i>All counselors are already in teams.</p>
                )}
                <Button onClick={handleAddMember} disabled={!memberForm.user_id} className="w-full">
                  <i className="fas fa-plus mr-2"></i> Add to Team
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMembersDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===== SUB-COMPONENTS =====

const UserCard = ({ userItem, currentUser, onEdit, onToggleStatus, onDelete, onResetPassword, onRedistribute, getRoleColor, getRoleDisplayName }) => (
  <Card className="hover:shadow-lg transition-shadow">
    <CardContent className="p-3">
      {/* User Info Header */}
      <div className="flex items-center gap-2 mb-2">
        <Avatar className="h-8 w-8">
          {userItem.profile_picture && <AvatarImage src={userItem.profile_picture} />}
          <AvatarFallback className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-[10px]">
            {userItem.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold truncate">{userItem.full_name}</h3>
          <p className="text-[10px] text-gray-500 truncate">{userItem.email}</p>
        </div>
      </div>
      
      {/* Role & Status - Single Row */}
      <div className="flex items-center justify-between gap-2 mb-2 text-[10px]">
        <div className="flex items-center gap-1">
          <span className="text-gray-500">Role:</span>
          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${getRoleColor(userItem.role)}`}>{getRoleDisplayName(userItem.role)}</Badge>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-gray-500">Status:</span>
          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${userItem.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {userItem.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </div>
      
      {/* Team (if assigned) */}
      {userItem.team_name && (
        <div className="flex items-center gap-1 mb-2 text-[10px]">
          <span className="text-gray-500">Team:</span>
          <span className="text-gray-800 truncate">{userItem.team_name}</span>
        </div>
      )}
      
      {/* Action Buttons - Compact Row */}
      <div className="flex gap-1 pt-2 border-t">
        <Button variant="outline" size="sm" className="flex-1 h-7 text-[10px] px-1" onClick={() => onEdit(userItem)} title="Edit">
          <i className="fas fa-edit"></i>
        </Button>
        <Button variant="outline" size="sm" className="flex-1 h-7 text-[10px] px-1" onClick={() => onToggleStatus(userItem)} title={userItem.is_active ? 'Deactivate' : 'Activate'}>
          <i className={`fas ${userItem.is_active ? 'fa-ban' : 'fa-check'}`}></i>
        </Button>
        {currentUser?.role === 'admin' && (
          <>
            {/* Redistribute Leads Button - Only for counselors/team leaders */}
            {['counselor', 'team_leader'].includes(userItem.role) && (
              <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-[10px] text-orange-600" onClick={() => onRedistribute(userItem)} title="Redistribute Leads" data-testid={`redistribute-${userItem.id}`}>
                <i className="fas fa-share-alt"></i>
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-[10px] text-blue-600" onClick={() => onResetPassword(userItem)} disabled={userItem.id === currentUser.id} title="Reset Password" data-testid={`reset-pwd-${userItem.id}`}>
              <i className="fas fa-key"></i>
            </Button>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-[10px] text-red-600" onClick={() => onDelete(userItem)} disabled={userItem.id === currentUser.id} title="Delete" data-testid={`delete-user-${userItem.id}`}>
              <i className="fas fa-trash"></i>
            </Button>
          </>
        )}
      </div>
    </CardContent>
  </Card>
);

const InactiveUserCard = ({ userItem, onDelete, onReactivate, isSubmitting, getRoleColor, getRoleDisplayName }) => (
  <Card className="opacity-70 hover:opacity-100 transition-opacity">
    <CardContent className="p-3">
      {/* User Info Header */}
      <div className="flex items-center gap-2 mb-2">
        <Avatar className="h-8 w-8 grayscale">
          <AvatarFallback className="bg-gray-400 text-white text-[10px]">
            {userItem.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold truncate">{userItem.full_name}</h3>
          <p className="text-[10px] text-gray-500 truncate">{userItem.email}</p>
        </div>
      </div>
      
      {/* Role & Status - Single Row */}
      <div className="flex items-center justify-between gap-2 mb-2 text-[10px]">
        <div className="flex items-center gap-1">
          <span className="text-gray-500">Role:</span>
          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${getRoleColor(userItem.role)}`}>{getRoleDisplayName(userItem.role)}</Badge>
        </div>
        <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-gray-100 text-gray-600">Inactive</Badge>
      </div>
      
      {/* Action Buttons - Compact Row */}
      <div className="flex gap-1 pt-2 border-t">
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1 h-7 text-[10px] px-2 text-green-600 hover:bg-green-50" 
          onClick={() => onReactivate(userItem)}
          disabled={isSubmitting}
          data-testid={`reactivate-user-${userItem.id}`}
        >
          <i className="fas fa-user-check mr-1"></i> Reactivate
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1 h-7 text-[10px] px-2 text-red-600 hover:bg-red-50" 
          onClick={() => onDelete(userItem)}
          disabled={isSubmitting}
          data-testid={`delete-user-${userItem.id}`}
        >
          <i className="fas fa-trash mr-1"></i> Delete
        </Button>
      </div>
    </CardContent>
  </Card>
);

const EmptyUsersState = ({ onAdd }) => (
  <Card>
    <CardContent className="text-center py-12">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <i className="fas fa-users text-gray-400 text-2xl"></i>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No active users found</h3>
      <p className="text-gray-500 mb-4">Start by adding your first team member</p>
      <Button onClick={onAdd} className="bg-indigo-600 hover:bg-indigo-700">
        <i className="fas fa-plus mr-2"></i> Add User
      </Button>
    </CardContent>
  </Card>
);

export default UserManagement;
